/**
 * Phase L — /mishap-corrector/unsupported
 *
 * Short intake for students whose target program isn't in our 5 verified
 * FL requirement graphs. Routes to Ivy League Consulting if target is Ivy /
 * elite / out-of-state, otherwise to Beyond Valencia.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

function SchoolTypeahead({ value, onChange, testId, placeholder }) {
  const [query, setQuery] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setItems([]); return; }
    setBusy(true);
    const t = setTimeout(() => {
      axios.get(`${API}/schools/directory`, { params: { q: query, limit: 12 } })
        .then((r) => { setItems(r.data.items || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(""); }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink placeholder:text-stone focus:outline-none focus:border-ivy"
        data-testid={testId}
      />
      {open && items.length > 0 && (
        <ul className="absolute z-10 top-full left-0 right-0 bg-bone border border-stone/40 border-t-0 max-h-60 overflow-y-auto" data-testid={`${testId}-results`}>
          {items.map((s) => (
            <li key={s.slug}>
              <button
                type="button"
                onMouseDown={() => { setQuery(s.name); onChange(s.slug); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-bone-dim text-sm flex items-baseline justify-between gap-3"
                data-testid={`${testId}-option-${s.slug}`}
              >
                <span className="text-ink">{s.name}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-stone">
                  {s.state}{s.is_ivy_or_elite ? " · elite" : s.is_florida ? " · FL" : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {busy && <div className="absolute right-3 top-2 text-[10px] text-stone">searching…</div>}
    </div>
  );
}

export default function MishapUnsupported() {
  const [form, setForm] = useState({
    target_school_slug: "", target_major: "",
    current_college_slug: "", current_college_other: "",
    what_went_wrong: "", email: "", first_name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.target_school_slug) { toast.error("Please pick a target school from the list"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.current_college_slug) delete payload.current_college_slug;
      if (!payload.current_college_other) delete payload.current_college_other;
      const r = await axios.post(`${API}/mishap/unsupported-request`, payload);
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const isPartner = result.routing_partner === "ivy_league_consulting";
    return (
      <div className="min-h-screen bg-bone" data-testid="mishap-unsupported-result">
        <SEO noindex title="Request received" canonical="/mishap-corrector/unsupported" />
        <TopNav />
        <main className="max-w-3xl mx-auto px-5 py-20">
          <div className={`border-l-4 ${isPartner ? "border-brass" : "border-ivy"} pl-6 py-4`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">
              {isPartner ? "Routed to partner" : "Beyond Valencia"}
            </div>
            <h1 className="mt-2 font-serif text-4xl text-ivy tracking-tight" data-testid="mishap-unsupported-result-h1">Request received.</h1>
            <p className="mt-4 text-ink-soft leading-relaxed" data-testid="mishap-unsupported-result-message">
              {result.next_steps_message}
            </p>
            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-stone">Lead ID: {result.lead_id}</div>
          </div>
          {isPartner && (
            <div className="mt-8">
              <Link
                to="/partners/ivy-league-consulting"
                className="inline-block border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6"
              >
                Learn about our partnership →
              </Link>
            </div>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="mishap-unsupported-page">
      <SEO
        title="Tell us about your transfer target"
        description="Short intake for community-college transfer students headed to programs outside our 5 verified Florida pathways."
        canonical="/mishap-corrector/unsupported"
      />
      <TopNav />
      <main className="max-w-2xl mx-auto px-5 py-14">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Mishap Corrector · Unlisted program</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-ivy tracking-tight leading-[1.05]">
          Tell us where you&apos;re trying to transfer.
        </h1>
        <p className="mt-6 text-ink-soft leading-relaxed">
          Our verified requirement graph covers 5 Florida programs today. For anything else, this shorter intake routes you to the right team — Beyond Valencia for Florida in-state targets, Ivy League Consulting for elite or out-of-state.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="mishap-unsupported-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Target school</label>
            <SchoolTypeahead
              value={form.target_school_slug}
              onChange={(slug) => setForm({ ...form, target_school_slug: slug })}
              testId="mishap-unsupported-school"
              placeholder="Start typing… (e.g. Harvard, Cornell, UF, UM)"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Target major</label>
            <input
              type="text" maxLength={120} required
              value={form.target_major}
              onChange={(e) => setForm({ ...form, target_major: e.target.value })}
              className="mt-2 w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink focus:outline-none focus:border-ivy"
              data-testid="mishap-unsupported-major"
              placeholder="e.g. Computer Science, Economics"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Current community college (optional)</label>
            <input
              type="text" maxLength={120}
              value={form.current_college_other}
              onChange={(e) => setForm({ ...form, current_college_other: e.target.value })}
              className="mt-2 w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink focus:outline-none focus:border-ivy"
              data-testid="mishap-unsupported-current-college"
              placeholder="e.g. Valencia College, Miami Dade College"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">What went wrong or what you need help with</label>
            <textarea
              rows={4} maxLength={500} required
              value={form.what_went_wrong}
              onChange={(e) => setForm({ ...form, what_went_wrong: e.target.value })}
              className="mt-2 w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink focus:outline-none focus:border-ivy"
              data-testid="mishap-unsupported-issue"
              placeholder="Tell us where you&apos;re stuck. 500 characters."
            />
            <div className="text-[10px] text-stone mt-1">{form.what_went_wrong.length}/500</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">First name</label>
              <input
                type="text" maxLength={60} required
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="mt-2 w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink focus:outline-none focus:border-ivy"
                data-testid="mishap-unsupported-first-name"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Email</label>
              <input
                type="email" required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink focus:outline-none focus:border-ivy"
                data-testid="mishap-unsupported-email"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-4 px-6 hover:bg-brass hover:border-brass disabled:opacity-50"
            data-testid="mishap-unsupported-submit"
          >
            {submitting ? "Submitting…" : "Route my request →"}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
